import React, { useState, useRef, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress
} from '@mui/material'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { getCurrentUser } from '../../../auth/ducks/selectors'
import { canEditBp } from '../../../../utils/user'
import APIService from '../../../../services/APIService'
import { getSharePointRedirectUri } from '../../../../utils/sharepoint'

const SharePointReauthModal = ({
  open,
  onClose,
  customViewId,
  buildingPassportId,
  bp,
  onSuccess
}) => {
  const api = APIService()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const currentUser = useSelector(getCurrentUser)
  const messageListenerRef = useRef(null)
  const listenerTimeoutRef = useRef(null)

  // Only building managers/admins should see this modal
  const canManage = canEditBp(currentUser, bp)

  // Clean up listeners on unmount
  useEffect(() => {
    return () => {
      if (messageListenerRef.current) {
        window.removeEventListener('message', messageListenerRef.current)
      }
      if (listenerTimeoutRef.current) {
        clearTimeout(listenerTimeoutRef.current)
      }
    }
  }, [])

  if (!canManage) {
    return null
  }

  const handleReauthenticate = async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await api.post(
        `/building-passport/${buildingPassportId}/custom-views/${customViewId}/sharepoint/reauth`,
        { redirect_uri: getSharePointRedirectUri() }
      )

      const data = response.data?.data || response.data
      if (data?.authorizationUrl) {
        // Remove any existing listener before adding a new one
        if (messageListenerRef.current) {
          window.removeEventListener('message', messageListenerRef.current)
        }
        if (listenerTimeoutRef.current) {
          clearTimeout(listenerTimeoutRef.current)
        }

        // Open OAuth popup
        window.open(
          data.authorizationUrl,
          'sharepoint-reauth',
          'width=800,height=600,scrollbars=yes,resizable=yes'
        )

        // Listen for OAuth completion
        const messageListener = event => {
          if (event.data.type === 'sharepoint_integration_success') {
            // Clean up listener
            window.removeEventListener('message', messageListener)
            messageListenerRef.current = null
            if (listenerTimeoutRef.current) {
              clearTimeout(listenerTimeoutRef.current)
              listenerTimeoutRef.current = null
            }

            setLoading(false)
            onSuccess?.()
          } else if (event.data.type === 'sharepoint_auth_token_missing') {
            setError(
              event.data.error ||
                'Your session has expired. Please close the popup window, log in and try again.'
            )
          }
        }

        window.addEventListener('message', messageListener)
        messageListenerRef.current = messageListener

        // Clean up listener after 10 minutes
        listenerTimeoutRef.current = setTimeout(() => {
          if (messageListenerRef.current) {
            window.removeEventListener('message', messageListenerRef.current)
            messageListenerRef.current = null
          }
          listenerTimeoutRef.current = null
          setLoading(false)
        }, 600000)
      } else {
        setError('Failed to initiate re-authentication')
        setLoading(false)
      }
    } catch (err) {
      setError(
        err.response?.data?.message || 'Failed to initiate re-authentication'
      )
      setLoading(false)
    }
  }

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        <Box display='flex' alignItems='center' gap={1}>
          <WarningAmberIcon color='warning' />
          <Typography variant='h6'>SharePoint Credentials Expired</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Alert severity='warning' sx={{ mb: 2 }}>
          <Typography variant='body2' sx={{ mb: 1 }}>
            Your SharePoint connection has expired. This can happen when:
          </Typography>
          <Box component='ul' sx={{ m: 0, pl: 2 }}>
            <li>Your password was changed or reset</li>
            <li>The refresh token has been revoked</li>
            <li>More than 90 days have passed since last authentication</li>
            <li>Azure AD security policies require re-authentication</li>
          </Box>
        </Alert>

        <Typography variant='body1'>
          Please re-authenticate to restore the SharePoint integration and
          resume syncing files.
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={handleReauthenticate}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Authenticating...' : 'Re-authenticate'}
        </Button>
      </DialogActions>
    </StyledDialog>
  )
}

export default SharePointReauthModal

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    max-width: 500px;
  }
`
